import { useNavigate } from 'react-router-dom';

import styled from '@emotion/styled';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportInfo } from '../project/CreateReport';
import BaseBlock, { TitleBlock } from '../ui/Block';
import { PageContainer } from '../ui/Container';
import useReports from './context/useReports';

import NewButton from '../ui/NewButton';
import CreateReportInformation from './CreateReportInformation';
import HomologationCNC from './HomologationCNC';
import ReportCard from './ReportCard';

const HomologationContainer = styled.div`
  width: 100%;
`;

const Block = styled(BaseBlock)`
  padding: 16px;
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
  flex-wrap: wrap;
  justify-content: flex-start;
  background-color: var(--lightgrey);
`;

const ReportTitleBlock = styled(TitleBlock)`
  position: relative;
  ::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 0;
    border-bottom: 16px solid white;
    border-right: 16px solid transparent;
  }
`;

const CncReportComponent = () => {
  const { t } = useTranslation(['dashboard', 'common', 'cnc-reports', 'model']);
  const navigate = useNavigate();
  const { reports, remove } = useReports();
  const [open, setOpen] = useState(false);

  if (!reports) return null;

  const handleNewProjectClicked = () => {
    setOpen(true);
  };

  const handleReportClicked = (reportId: string) => () => {
    navigate(`/cnc-reports/${reportId}`);
  };

  const handleReportDeleted = (reportId: string) => async (event: any) => {
    return await remove(reportId);
  };

  return (
    <PageContainer bgColor="white">
      <HomologationContainer>
        <HomologationCNC />
      </HomologationContainer>
      <ReportTitleBlock background="var(--yellow)">
        <h3>{t('cnc-reports:title')}</h3>
        <p>{t('cnc-reports:subtitle')}</p>
      </ReportTitleBlock>
      {open && (
        <CreateReportModal
          open={open}
          onClose={() => {
            setOpen(false);
          }}
        />
      )}
      <Block>
        <NewButton
          onClick={handleNewProjectClicked}
          createdObjectLabel={t('cnc-reports:add-report')}
          bgColor="var(--yellow)"
        />
        {reports.map((report) => (
          <ReportCard
            report={report}
            key={report._id.toString()}
            onClick={handleReportClicked(report._id.toString())}
            onDelete={handleReportDeleted(report._id.toString())}
          />
        ))}
      </Block>
    </PageContainer>
  );
};

interface CreateReportModalProps {
  open: boolean;
  onClose: () => void;
}

const CreateReportModal = ({ open, onClose }: CreateReportModalProps) => {
  const { t } = useTranslation(['cnc-reports', 'common', 'model']);
  const navigate = useNavigate();
  const { create } = useReports();

  const handleClose = () => {
    onClose && onClose();
  };

  const handleNext = async (values: ReportInfo) => {
    if (!values) return;
    const result = await create({
      ...values,
    });
    handleClose();
    navigate(`/cnc-reports/${result._id}`);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="body"
      id="create_modal"
      maxWidth="lg"
    >
      <DialogTitle id="scroll-dialog-title">
        {t('cnc-reports:creation-modal.title')}
      </DialogTitle>
      <DialogContent dividers={true}>
        <CreateReportInformation
          onNext={handleNext}
          onClose={handleClose}
          validationButtonLabel={'cnc-reports:creation-modal.save'}
        />
      </DialogContent>
    </Dialog>
  );
};

export default CncReportComponent;
